import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { EndpointConfig } from './types'
import type { BotMessage } from '@/composables/useSamGptCompletions/types'
import { useUserStore } from '@/stores/userStore'

export const useAppChatStore = defineStore('appChat', () => {
  const { t, locale } = useI18n()
  const userStore = useUserStore()

  const endpointConfig = ref<EndpointConfig>({
    screen: 'PDP',
    entryNameAlias: `PDP_CHAT_WEB_${locale.value.toUpperCase()}`,
    startFlow: false,
    shouldCallBotForGreeting: false
  })

  const isChatWindowOpen = ref<boolean>(false)
  const hideChatInputField = ref<boolean>(false)
  const messageChipsOptions = ref<Array<any>>([])
  const chatGreeting = ref<Array<BotMessage>>()
  const contactFormConfig = ref<Record<string, any>>({
    contactFormTitleCopy: '',
    contactFormCtaCopy: '',
    userInitialMessage: ''
  })
  const samButtonTooltip = ref<Record<string, any>>({
    title: t('samGpt.samButtonTooltipPdp.title'),
    body: t('samGpt.samButtonTooltipPdp.body'),
    timeOut: 2000
  })

  const showForm = computed(() => {
    return !userStore.isAuthenticated
  })

  const updateHideChatInputField = (value: boolean) => {
    hideChatInputField.value = value
  }

  const updateMessagesChips = (value: Array<any>) => {
    messageChipsOptions.value = value
  }

  const updateChatGreeting = (value: Array<BotMessage>) => {
    chatGreeting.value = value
  }

  const updateContactFormConfig = (value: Record<string, any>) => {
    contactFormConfig.value = value
  }

  const updateSamButtonTooltip = (value: Record<string, any>) => {
    samButtonTooltip.value = value
  }

  const updateEndpointConfig = (value: EndpointConfig) => {
    endpointConfig.value = value
  }

  const openChatWindow = (endpointConfig: EndpointConfig, greetingMessages: Array<BotMessage> = []) => {
    updateEndpointConfig(endpointConfig)
    if (greetingMessages.length) updateChatGreeting(greetingMessages)
    isChatWindowOpen.value = true
    window.dataLayer?.push({
      event: 'lead-creation-requested',
      phone: userStore?.user?.phone,
      screen: endpointConfig.screen,
      screen_cta: endpointConfig.entryNameAlias
    })
  }

  const closeChatWindow = () => {
    isChatWindowOpen.value = false
  }

  return {
    isChatWindowOpen,
    openChatWindow,
    closeChatWindow,
    showForm,
    messageChipsOptions,
    updateMessagesChips,
    hideChatInputField,
    updateHideChatInputField,
    chatGreeting,
    updateChatGreeting,
    contactFormConfig,
    updateContactFormConfig,
    samButtonTooltip,
    updateSamButtonTooltip,
    endpointConfig,
    updateEndpointConfig
  }
})
